import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from 'containers/Ribbon/ShelvesRibbon/reducer';
import { ShelfSelection } from 'lib/modelBuilder/entities/ShelfSelection';
import { makeSelectShelvesSelected } from 'containers/Configurator/selectors/selection';
import { makeSelectShelvesSelectedLastHistory } from 'containers/Configurator/selectors/history';
import { Color } from 'lib/modelBuilder/entities/Color';
import { ShelfComposition } from 'lib/modelBuilder/constants';
import { makeSelectIsSameDrawersNumberInShelvesSelected } from 'containers/DrawersPanel/selectors';
import { makeSelectContext } from '../../Configurator/selectors/general';
import { FurnitureEditorContext } from '../../../lib/constraintsChecker/FurnitureEditor';
import { marksHeight } from './constants';

/**
 * Direct selector to the shelvesRibbon state domain
 */

const selectShelvesRibbonDomain = (state: ApplicationRootState) => {
  return state.shelvesRibbon || initialState;
};

export const makeSelectMenuOpen = () =>
  createSelector(selectShelvesRibbonDomain, substate => {
    return substate.menuOpen;
  });

export const makeSelectShelfSelectionProfile = () =>
  createSelector(
    selectShelvesRibbonDomain,
    (substate): ShelfSelection => substate.selection,
  );

export const makeSelectCommonHeight = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelected(),
    (substate, shelves): number | undefined => {
      if (shelves.length === 0) return undefined;
      // throw new Error('There must be at least one shelf in cabinet');
      const height = shelves[0].height;
      for (const shelf of shelves) {
        if (height !== shelf.height) {
          return undefined;
        }
      }
      return height;
    },
  );

export const makeSelectHaveOneDoor = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelectedLastHistory(),
    (substate, shelves): boolean => {
      if (shelves.length === 0) return false;

      for (const shelf of shelves) {
        if (shelf.doors.length > 0) return true;
      }
      return false;
    },
  );

export const makeSelectHaveFourDoors = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelectedLastHistory(),
    (substate, shelves): boolean => {
      if (shelves.length === 0) return false;

      for (const shelf of shelves) {
        if (shelf.composition === ShelfComposition.FourDoors) return true;
      }
      return false;
    },
  );

export const makeSelectHaveFourDoorsOnly = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelectedLastHistory(),
    (substate, shelves): boolean => {
      if (shelves.length === 0) return false;
      for (const shelf of shelves) {
        if (shelf.composition !== ShelfComposition.FourDoors) return false;
      }
      return true;
    },
  );

export const makeSelectHaveOneDrawer = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelectedLastHistory(),
    (substate, shelves): boolean => {
      if (shelves.length === 0) return false;

      for (const shelf of shelves) {
        if (shelf.drawers.length > 0) return true;
      }
      return false;
    },
  );

export const makeSelectHaveCommonDrawers = () =>
  createSelector(
    selectShelvesRibbonDomain,
    makeSelectShelvesSelectedLastHistory(),
    (substate, shelves): boolean => {
      if (shelves.length === 0) return false;
      for (const shelf of shelves) {
        if (shelf.drawers.length === 0) return false;
      }
      return true;
    },
  );

export const makeSelectCommonDrawersColors = () =>
  createSelector(
    makeSelectIsSameDrawersNumberInShelvesSelected(),
    makeSelectShelvesSelectedLastHistory(),
    (sameDrawers, shelves): Color[] => {
      const colors: Color[] = [];
      if (sameDrawers) {
        for (const shelf of shelves) {
          for (const drawer of shelf.drawers) {
            const position = drawer.position as number;
          }
        }
      }

      return colors;
    },
  );

export const makeSelectMarkHeight = () =>
  createSelector(makeSelectContext(), (context): any[] => {
    if (context === FurnitureEditorContext.webShop) {
      return [...marksHeight.slice(1)];
    }

    return marksHeight;
  });
